import { BackLink, Intro } from "components/commons";
import locale from "localization";
import { StationContext, UserContext } from "contexts";
import { useContext, useState } from "react";
import { FormMode, Path } from "enums";
import ExpenseForm from "./expense-form";
import { useAlert, useApi, useModal, useRouter, useMount } from "hooks";
import { FormProvider } from "contexts/form-context";
import initialState from "./expense-form.state";
import { createExpenseApi, getExpenseByIdApi, uploadFileApi } from "apis";
import { useMemo, useCallback } from "react";
import ViewExpense from "./view-expense";
import { appendSizeToFilename } from "utils";
import { formatAmount, stringToDecimal } from "utils/text.utils";
import { SuccessModal } from "components/modals";
import { AddExpense, AddExpenseSuccess } from "images";

const ExpenseDetailsModule = () => {
  const { station } = useContext(StationContext);
  const { user } = useContext(UserContext);

  const { request: createExpense, loading: isSaving } = useApi({
    api: createExpenseApi,
    modalError: true,
    handleOwnError: true,
    cleanObject: false,
  });
  const { request: uploadFile, loading: isUploading } = useApi({
    api: uploadFileApi,
  });
  const { request: getExpenseById, result: expense } = useApi({
    api: getExpenseByIdApi,
  });

  const [form, setForm] = useState(null);
  const { addAlert } = useAlert();
  const confirmModal = useModal();
  const successModal = useModal();
  const router = useRouter();
  const expenseId = router.query?.id;
  const formMode = router.query?.mode || router.query?.pageMode || FormMode.Add;
  const isAdd = formMode === FormMode.Add;

  const handleOnSubmit = useCallback(
    async (fields) => {
      confirmModal.close();
      let slip = fields?.slip?.value;
      const uploadedUrls = [];

      const slipArray = Array.isArray(slip) ? slip : slip ? [slip] : [];

      for (const item of slipArray) {
        if (item instanceof File) {
          const renamedFile = appendSizeToFilename(item);

          const formData = new FormData();
          formData.append("file", renamedFile);

          try {
            const resUpload = await uploadFile({ body: formData });

            if (resUpload?.fileUrl) {
              uploadedUrls.push(resUpload.fileUrl);
            }
          } catch (error) {
            return addAlert({
              title: locale.imageUploadFailed,
              subtitle: error,
              type: "error",
              interval: 10000,
            });
          }
        } else if (typeof item === "string") {
          uploadedUrls.push(item);
        }
      }

      const imageUrl = uploadedUrls.join(",");

      const payload = {
        stationCode: station.stationCode,
        type: fields.type?.value,
        userId: user?.userId,
        amount: stringToDecimal(fields?.amount?.value),
        remarks: fields?.remarks?.value || "",
        imageUrl: imageUrl || "",
        shiftId: fields?.shiftId?.value,
        referenceNumber: fields?.referenceNumber?.value || "",
        rebateType: fields?.rebateType?.value || "",
      };

      const res = await createExpense(payload);

      if (!res?.error) {
        successModal.show();
      }
    },
    [
      addAlert,
      createExpense,
      station.stationCode,
      confirmModal,
      successModal,
      uploadFile,
      user?.userId,
    ]
  );

  useMount(async () => {
    if (!isAdd) {
      await getExpenseById({ id: expenseId }, { useLoading: true });
    }
  });

  return (
    <>
      <BackLink text={locale.expenseLogs} path={Path.Expense} />
      <Intro title={isAdd ? locale.addExpense : ""} />
      <FormProvider
        initialState={useMemo(() => initialState(expense), [expense])}
        onChange={(form) => setForm(form)}
        onSubmit={() => {
          confirmModal.show();
        }}
      >
        {isAdd ? (
          <ExpenseForm loading={isSaving || isUploading} expense={expense} />
        ) : (
          <ViewExpense expense={expense} />
        )}
      </FormProvider>
      <SuccessModal
        {...confirmModal}
        image={AddExpense}
        title={`${locale.addExpense}?`}
        content={locale.expenseConfirmMessage}
        centerButtons={true}
        contentHighlight={[
          formatAmount(stringToDecimal(form?.fields?.amount?.value || 0)),
          form?.fields?.type?.value ? ` for ${form?.fields?.type?.value}` : "",
        ]}
        close={() => {
          confirmModal.close();
        }}
        secondary={{
          text: locale.cancel,
          onClick: () => {
            confirmModal.close();
          },
        }}
        primary={{
          text: locale.proceed,
          onClick: () => handleOnSubmit(form?.fields),
        }}
      />
      <SuccessModal
        {...successModal}
        closable={false}
        image={AddExpenseSuccess}
        title={locale.expensesSubmitted}
        content={locale.youHaveSuccessfullyAddedAnExpense}
        centerButtons={true}
        primary={{
          text: locale.gotIt,
          onClick: () => router.push(Path.Expense),
        }}
      />
    </>
  );
};

export default ExpenseDetailsModule;
